import React, { FC, useCallback, useMemo } from 'react';
import { Checkbox } from 'src/common';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { INotificationSetting, isNotificationSettingsMode } from 'src/models/users/NotificationSettings/types';
import { NotificationType, NotificationSettingMode } from '@tendium/prom-types/notifications';
import { useLoadAllMps } from 'src/models/matchingProfiles/hooks';
import { useUpdateNotificationSettings } from 'src/models/users/NotificationSettings/hooks';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useWorkspaces } from 'src/models/workspace/hooks';

interface IProps {
  setting: INotificationSetting;
}

export const NotificationSettings: FC<IProps> = ({ setting }) => {
  const { name, itemId, notificationSettingValues: values } = setting;
  const { t } = useTranslation();

  const [updateSettings] = useUpdateNotificationSettings();

  const { data: mps } = useLoadAllMps();
  const { data: wss } = useWorkspaces();

  const mp = useMemo(
    () => (mps && itemId && name === NotificationType.mpDailySendout ? mps.find(_ => _.id === itemId) : undefined),
    [itemId, name, mps]
  );

  const ws = useMemo(
    () => (wss && itemId && name === NotificationType.tenderUpdate ? wss.find(_ => _.id === itemId) : undefined),
    [itemId, name, wss]
  );

  const inAppValue = useMemo(() => {
    return values.find(v => v.mode === NotificationSettingMode.InApp)?.value;
  }, [values]);

  const emailValue = useMemo(() => {
    return values.find(v => v.mode === NotificationSettingMode.Email)?.value || false;
  }, [values]);

  const onValuesChange = useCallback(
    (e: CheckboxChangeEvent) => {
      const { checked: value, name: mode } = e.target;

      if (isNotificationSettingsMode(mode)) {
        updateSettings({
          name: Object.keys(NotificationType)[
            Object.values(NotificationType).indexOf(name)
          ] as keyof typeof NotificationType,
          mode: mode || '',
          value,
          itemId
        });
      }
    },
    [itemId, name, updateSettings]
  );
  const itemName = useMemo(
    () =>
      itemId &&
      (name === NotificationType.mpDailySendout && mp
        ? mp.name || t('Tenders.matchingProfileDefaultName')
        : name === NotificationType.tenderUpdate && ws
        ? ws.name
        : undefined),
    [itemId, mp, name, t, ws]
  );

  return (
    <li className={styles.setting}>
      <h5 className={styles.settingName}>
        <span>
          {itemName ??
            t(
              `Notifications.notificationSettings_${
                Object.keys(NotificationType)[Object.values(NotificationType).indexOf(name)]
              }`
            )}
        </span>
      </h5>
      <div className={styles.settingActions}>
        {inAppValue !== undefined && (
          <Checkbox checked={inAppValue} name={NotificationSettingMode.InApp} onChange={onValuesChange}>
            {t(`Notifications.notificationSettings${NotificationSettingMode.InApp}`)}
          </Checkbox>
        )}
        <Checkbox checked={emailValue} name={NotificationSettingMode.Email} onChange={onValuesChange}>
          {t(`Notifications.notificationSettings${NotificationSettingMode.Email}`)}
        </Checkbox>
      </div>
    </li>
  );
};

export default NotificationSettings;
